import { animate, inView, stagger, scroll, hover, press } from "motion";

/***************************************
 * Animatie presets
 * Gebruik via data-animate="[naam]" in HTML of PHP
 ***************************************/
const presets = {
  "fade-in": { opacity: [0, 1] },
  "fade-up": { opacity: [0, 1], y: [40, 0] },
  "fade-down": { opacity: [0, 1], y: [-40, 0] },
  "fade-left": { opacity: [0, 1], x: [40, 0] },
  "fade-right": { opacity: [0, 1], x: [-40, 0] },
  "scale-up": { opacity: [0, 1], scale: [0.85, 1] },
  "blur-in": { opacity: [0, 1], filter: ["blur(12px)", "blur(0px)"] },
  "slide-up": { opacity: [0, 1], y: ["60px", "0px"] },
};

const defaultEasing = [0.25, 0.1, 0.25, 1];
const viewMargin = "-80px";

/***************************************
 * splitText — Custom implementatie (Motion+ splitText is premium)
 * Splits tekst in chars of words voor stagger-animaties
 * Gebruik: data-split-text="chars" of "words" + data-animate="split-chars" of "split-words"
 ***************************************/
function splitTextElement(el, by = "chars") {
  const text = el.textContent.trim();
  if (!text) return { chars: [], words: [] };

  el.innerHTML = "";
  const chars = [];
  const words = [];

  if (by === "chars") {
    [...text].forEach((char, i) => {
      const span = document.createElement("span");
      span.className = "split-char";
      span.setAttribute("data-index", String(i));
      span.style.cssText = "display:inline-block;opacity:0";
      span.textContent = char;
      el.appendChild(span);
      chars.push(span);
    });
    return { chars, words };
  }

  if (by === "words") {
    text.split(/\s+/).forEach((word, i) => {
      const span = document.createElement("span");
      span.className = "split-word";
      span.setAttribute("data-index", String(i));
      span.style.cssText = "display:inline-block;margin-right:0.25em;opacity:0";
      span.textContent = word;
      el.appendChild(span);
      words.push(span);
    });
    return { chars: [], words };
  }

  return { chars: [], words: [] };
}

/***************************************
 * Scroll-linked animaties (Motion scroll())
 * Gebruik via data-animate-scroll="[naam]" in HTML of PHP
 * Opties: data-scroll-speed (parallax), data-scroll-offset
 ***************************************/
function initScrollAnimations() {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  document.querySelectorAll("[data-animate-scroll]").forEach((el) => {
    const effect = el.dataset.animateScroll;
    const speed = parseFloat(el.dataset.scrollSpeed || "-0.2");

    if (effect === "parallax") {
      scroll(
        (progress) => {
          const y = progress * speed * 100;
          el.style.transform = `translateY(${y}%)`;
        },
        {
          target: el,
          offset: ["start end", "end start"],
        }
      );
    }

    if (effect === "fade-scroll") {
      scroll(
        (progress) => {
          el.style.opacity = String(progress);
        },
        {
          target: el,
          offset: ["start end", "center center"],
        }
      );
    }

    if (effect === "scale-scroll") {
      scroll(
        (progress) => {
          const scale = 0.7 + progress * 0.3;
          el.style.transform = `scale(${scale})`;
        },
        {
          target: el,
          offset: ["start end", "center center"],
        }
      );
    }

    if (effect === "rotate-scroll") {
      scroll(
        (progress) => {
          const deg = progress * 360;
          el.style.transform = `rotate(${deg}deg)`;
        },
        {
          target: el,
          offset: ["start end", "end start"],
        }
      );
    }
  });
}

/***************************************
 * Amount counter
 * Telt van 0 naar data-count in 2 seconden zodra het getal in beeld komt
 ***************************************/
function parseCountValue(raw) {
  const value = String(raw).trim();
  const match = value.match(/^(\D*?)(-?\d[\d.,\s]*)(\D*)$/);
  if (!match) return null;

  const prefix = match[1];
  const suffix = match[3];
  const digits = match[2].replace(/\s/g, "");
  const commaCount = (digits.match(/,/g) || []).length;
  const dotCount = (digits.match(/\./g) || []).length;
  const lastComma = digits.lastIndexOf(",");
  const lastDot = digits.lastIndexOf(".");

  let thousandsSep = "";
  let decimalSep = "";
  let normalized = digits;
  let decimals = 0;

  const fractionLength = (separatorIndex) => digits.length - separatorIndex - 1;

  if (commaCount && dotCount) {
    if (lastComma > lastDot) {
      thousandsSep = ".";
      decimalSep = ",";
      decimals = fractionLength(lastComma);
      normalized = digits.replace(/\./g, "").replace(",", ".");
    } else {
      thousandsSep = ",";
      decimalSep = ".";
      decimals = fractionLength(lastDot);
      normalized = digits.replace(/,/g, "");
    }
  } else if (commaCount > 1) {
    thousandsSep = ",";
    normalized = digits.replace(/,/g, "");
  } else if (dotCount > 1) {
    thousandsSep = ".";
    normalized = digits.replace(/\./g, "");
  } else if (commaCount === 1) {
    const fraction = fractionLength(lastComma);
    if (fraction === 3) {
      thousandsSep = ",";
      normalized = digits.replace(/,/g, "");
    } else {
      decimalSep = ",";
      decimals = fraction;
      normalized = digits.replace(",", ".");
    }
  } else if (dotCount === 1) {
    const fraction = fractionLength(lastDot);
    if (fraction === 3) {
      thousandsSep = ".";
      normalized = digits.replace(/\./g, "");
    } else {
      decimalSep = ".";
      decimals = fraction;
    }
  }

  if (!thousandsSep && /\d\s+\d/.test(match[2])) {
    thousandsSep = " ";
  }

  const target = Number(normalized);
  if (!Number.isFinite(target)) return null;

  return { target, decimals, prefix, suffix, thousandsSep, decimalSep };
}

function formatCount(value, format) {
  const rounded = format.decimals > 0 ? value.toFixed(format.decimals) : String(Math.round(value));
  const [intPart, decPart] = rounded.split(".");
  const grouped = format.thousandsSep
    ? intPart.replace(/\B(?=(\d{3})+(?!\d))/g, format.thousandsSep)
    : intPart;
  const decimal = decPart !== undefined ? `${format.decimalSep}${decPart}` : "";

  return `${format.prefix}${grouped}${decimal}${format.suffix}`;
}

function initCountUp() {
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  document.querySelectorAll(".home-ambassador__info-block-amount").forEach((el) => {
    const format = parseCountValue(el.dataset.count || el.textContent);
    if (!format) return;

    if (reduceMotion) {
      el.textContent = formatCount(format.target, format);
      return;
    }

    el.textContent = formatCount(0, format);

    inView(
      el,
      () => {
        if (el.dataset.counted) return;
        el.dataset.counted = "1";

        animate(0, format.target, {
          duration: 2,
          ease: "easeOut",
          onUpdate: (latest) => {
            el.textContent = formatCount(latest, format);
          },
          onComplete: () => {
            el.textContent = formatCount(format.target, format);
          },
        });
      },
      { margin: "0px", amount: "some" }
    );
  });
}

/***************************************
 * Initialiseer animaties
 ***************************************/
function initAnimations() {
  initCountUp();

  // Sla over als gebruiker reduced motion heeft ingesteld
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  // Reguliere animaties
  document.querySelectorAll("[data-animate]").forEach((el) => {
    const effect = el.dataset.animate;
    if (effect === "stagger-up") return; // apart afgehandeld

    const delay = parseFloat(el.dataset.delay || 0) / 1000;
    const duration = parseFloat(el.dataset.duration || 700) / 1000;
    const useSpring = el.dataset.animateType === "spring";
    const keyframes = presets[effect] ?? presets["fade-up"];

    inView(
      el,
      () => {
        if (el.dataset.animated) return;
        el.dataset.animated = "1";
        const options = useSpring
          ? { delay, type: "spring", stiffness: 300, damping: 30 }
          : { delay, duration, easing: defaultEasing };
        animate(el, keyframes, options);
      },
      { margin: viewMargin }
    );
  });

  // Stagger animaties — children animeren één voor één
  document.querySelectorAll('[data-animate="stagger-up"]').forEach((parent) => {
    const children = Array.from(parent.children);
    const delay = parseFloat(parent.dataset.delay || 0) / 1000;
    const duration = parseFloat(parent.dataset.duration || 700) / 1000;

    inView(
      parent,
      () => {
        if (parent.dataset.animated) return;
        parent.dataset.animated = "1";
        animate(
          children,
          { opacity: [0, 1], y: [30, 0] },
          {
            delay: stagger(0.1, { start: delay }),
            duration,
            easing: defaultEasing,
          }
        );
      },
      { margin: viewMargin }
    );
  });

  // Scroll-linked animaties
  initScrollAnimations();

  // Split text animaties
  initSplitTextAnimations();

  // Hover & press gesture animaties
  initGestureAnimations();
}

/***************************************
 * Split text — chars/words stagger bij scroll
 * data-split-text="chars" of "words", data-split-stagger (default 0.05)
 ***************************************/
function initSplitTextAnimations() {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  document.querySelectorAll("[data-split-text]").forEach((el) => {
    if (el.dataset.splitDone) return;

    const by = el.dataset.splitText || "chars";
    const staggerDelay = parseFloat(el.dataset.splitStagger || 50) / 1000;
    const duration = parseFloat(el.dataset.duration || 700) / 1000;

    inView(
      el,
      () => {
        if (el.dataset.splitDone) return;
        el.dataset.splitDone = "1";

        const { chars, words } = splitTextElement(el, by);
        const targets = by === "chars" ? chars : words;
        if (targets.length === 0) return;

        animate(targets, { opacity: [0, 1], y: [20, 0] }, {
          duration,
          delay: stagger(staggerDelay, { start: 0 }),
          easing: defaultEasing,
        });
      },
      { margin: viewMargin }
    );
  });
}

/***************************************
 * Hover & Press gesture animaties
 * data-animate-hover="scale" | "lift" | "rotate"
 * data-animate-press="scale" | "press"
 ***************************************/
function initGestureAnimations() {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  document.querySelectorAll("[data-animate-hover]").forEach((el) => {
    const effect = el.dataset.animateHover;
    const scale = parseFloat(el.dataset.hoverScale || 1.05);
    const rotate = parseFloat(el.dataset.hoverRotate || 5);

    hover(el, () => {
      if (effect === "scale") {
        animate(el, { scale }, { type: "spring", stiffness: 400, damping: 25 });
        return () => animate(el, { scale: 1 }, { type: "spring", stiffness: 400, damping: 25 });
      }
      if (effect === "lift") {
        animate(el, { y: -8 }, { type: "spring", stiffness: 300, damping: 25 });
        return () => animate(el, { y: 0 }, { type: "spring", stiffness: 300, damping: 25 });
      }
      if (effect === "rotate") {
        animate(el, { rotate }, { type: "spring", stiffness: 300, damping: 20 });
        return () => animate(el, { rotate: 0 }, { type: "spring", stiffness: 300, damping: 20 });
      }
    });
  });

  document.querySelectorAll("[data-animate-press]").forEach((el) => {
    const effect = el.dataset.animatePress;
    const scale = parseFloat(el.dataset.pressScale || 0.95);

    press(el, () => {
      if (effect === "scale" || effect === "press") {
        animate(el, { scale }, { type: "spring", stiffness: 500, damping: 30 });
        return () => animate(el, { scale: 1 }, { type: "spring", stiffness: 400, damping: 25 });
      }
    });
  });
}

initAnimations();
